// v5-shaped Auth facade backed by aws-amplify v6.
//
// The bundled authenticator under src/plugins/aws-amplify-vue/ was forked
// from the Amplify v2-era Vue plugin and drives Cognito through
// this.$Amplify.Auth.* with v5 method signatures (stateful user object,
// challengeName-driven branching, getJwtToken() session shape). v6 made
// auth stateless and nextStep-driven, so we expose this shim as
// $Amplify.Auth / $auth and keep the components unchanged.
//
// Anything new should call aws-amplify/auth directly instead of going
// through this shim.

import {
    signIn as v6SignIn,
    signUp as v6SignUp,
    signOut as v6SignOut,
    confirmSignIn as v6ConfirmSignIn,
    confirmSignUp as v6ConfirmSignUp,
    resendSignUpCode,
    resetPassword,
    confirmResetPassword,
    signInWithRedirect,
    getCurrentUser,
    fetchAuthSession,
    fetchUserAttributes,
    setUpTOTP,
    verifyTOTPSetup,
    updateMFAPreference,
    fetchMFAPreference,
} from 'aws-amplify/auth'

// v6 dropped the SignInWithApple alias — Apple is just 'Apple' now.
function normalizeProvider(p) {
    if (p === 'SignInWithApple') return 'Apple'
    return p
}

function challengeNameFromStep(step) {
    if (!step) return null
    switch (step.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
            return 'SOFTWARE_TOKEN_MFA'
        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
            return 'SMS_MFA'
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
            return 'NEW_PASSWORD_REQUIRED'
        case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
            return 'MFA_SETUP'
        default:
            return null
    }
}

function v5Error(code, message) {
    const err = new Error(message)
    err.code = code
    err.name = code
    return err
}

// v5's currentAuthenticatedUser / signIn success returned a rich object with
// signInUserSession.{idToken,accessToken,refreshToken}.{jwtToken,payload}.
// Nav.vue reads user.signInUserSession.idToken.payload['cognito:groups' /
// 'sub' / 'email' / 'preferred_username' / 'forumToken' / 'discussToken' /
// 'intercomHash' / 'identities'] and .accessToken.payload.device_key in
// many places — reproduce that shape here so the callsites keep working.
// v5's CognitoUser carried `preferredMFA` ('SOFTWARE_TOKEN_MFA' | 'SMS_MFA'
// | 'NOMFA'), and Nav, UserProfileModal and dataSamples all gate the MFA
// switch and the MFA prompts on `user.preferredMFA == 'SOFTWARE_TOKEN_MFA'`.
// v6 moved it to fetchMFAPreference(), which returns { preferred: 'TOTP' }.
function v5PreferredMFA(pref) {
    switch (pref && pref.preferred) {
        case 'TOTP':
            return 'SOFTWARE_TOKEN_MFA'
        case 'SMS':
            return 'SMS_MFA'
        default:
            return 'NOMFA'
    }
}

async function hydrateUser() {
    const user = await getCurrentUser()
    // Both calls are GetUser round trips; run them together.
    const [attributes, mfaPreference] = await Promise.all([
        fetchUserAttributes().catch(() => ({})),
        fetchMFAPreference().catch(() => null),
    ])
    let session = null
    try {
        session = await fetchAuthSession({ forceRefresh: false })
    } catch (e) {
        session = null
    }
    const idToken = session?.tokens?.idToken
    const accessToken = session?.tokens?.accessToken
    return {
        ...user,
        username: user.username,
        attributes,
        preferredMFA: v5PreferredMFA(mfaPreference),
        signInUserSession: session?.tokens
            ? {
                  idToken: {
                      jwtToken: idToken?.toString() || '',
                      payload: idToken?.payload || {},
                      getJwtToken: () => idToken?.toString() || '',
                  },
                  accessToken: {
                      jwtToken: accessToken?.toString() || '',
                      payload: accessToken?.payload || {},
                      getJwtToken: () => accessToken?.toString() || '',
                  },
                  refreshToken: { token: null, getToken: () => null },
              }
            : null,
    }
}

const AuthShim = {
    async signIn(username, password, options) {
        // v5: Auth.signIn(username, password, clientMetadata)
        // v6: signIn({ username, password, options: { clientMetadata } })
        const result = await v6SignIn({
            username,
            password,
            options: options ? { clientMetadata: options } : undefined,
        })
        if (result.isSignedIn) {
            try {
                const u = await hydrateUser()
                return { ...u, challengeName: null }
            } catch (e) {
                return { username, challengeName: null }
            }
        }
        const challengeName = challengeNameFromStep(result.nextStep)
        if (challengeName) {
            return {
                username,
                challengeName,
                challengeParam: result.nextStep,
            }
        }
        // CONFIRM_SIGN_UP — v5 threw UserNotConfirmedException, callers
        // branch on .code, so reshape into the same throw.
        if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
            throw v5Error('UserNotConfirmedException', 'User is not confirmed.')
        }
        if (result.nextStep?.signInStep === 'RESET_PASSWORD') {
            throw v5Error('PasswordResetRequiredException', 'Password reset required for the user')
        }
        return { username, challengeName: null, nextStep: result.nextStep }
    },

    async confirmSignIn(_user, code, _challengeName) {
        // v6 is stateless — the pending challenge is held inside the
        // amplify auth singleton, so user/challengeName are ignored.
        const result = await v6ConfirmSignIn({ challengeResponse: code })
        if (result.isSignedIn) {
            try {
                return await hydrateUser()
            } catch (e) {
                return { username: _user?.username }
            }
        }
        return {
            username: _user?.username,
            challengeName: challengeNameFromStep(result.nextStep),
            challengeParam: result.nextStep,
        }
    },

    async signUp(params) {
        // v5: signUp({ username, password, attributes, clientMetadata })
        // v6: signUp({ username, password, options: { userAttributes, clientMetadata } })
        const { username, password, attributes, clientMetadata } = params
        const result = await v6SignUp({
            username,
            password,
            options: {
                userAttributes: attributes,
                ...(clientMetadata ? { clientMetadata } : {}),
            },
        })
        // SignUp.vue reads data.user.username and data.userConfirmed.
        return {
            user: { username },
            userConfirmed: result.isSignUpComplete,
            userSub: result.userId,
            nextStep: result.nextStep,
        }
    },

    confirmSignUp(username, code) {
        return v6ConfirmSignUp({ username, confirmationCode: code })
    },

    resendSignUp(username) {
        return resendSignUpCode({ username })
    },

    forgotPassword(username) {
        return resetPassword({ username })
    },

    forgotPasswordSubmit(username, code, newPassword) {
        return confirmResetPassword({
            username,
            confirmationCode: code,
            newPassword,
        })
    },

    signOut() {
        return v6SignOut()
    },

    async currentAuthenticatedUser(_opts) {
        return await hydrateUser()
    },

    async currentSession() {
        // v5 session: getIdToken().getJwtToken(), getAccessToken().getJwtToken().
        // Callers (appsync.js, VerifyMfaModal, MfaPromptModal) consume exactly
        // those two paths.
        const session = await fetchAuthSession({ forceRefresh: false })
        if (!session.tokens) {
            // Match v5's behavior of throwing when no session — appsync.js
            // catches and returns '' for the JWT.
            throw v5Error('NotAuthorizedException', 'No current user')
        }
        const idToken = session.tokens.idToken
        const accessToken = session.tokens.accessToken
        return {
            getIdToken: () => ({
                getJwtToken: () => idToken?.toString() || '',
                payload: idToken?.payload || {},
            }),
            getAccessToken: () => ({
                getJwtToken: () => accessToken?.toString() || '',
                payload: accessToken?.payload || {},
            }),
            getRefreshToken: () => ({ getToken: () => null }),
        }
    },

    async setupTOTP(_user) {
        // v5 returned the secret string directly; the SetMFA component
        // concatenates it into an otpauth:// URL by hand.
        const details = await setUpTOTP()
        return details.sharedSecret
    },

    async verifyTotpToken(_user, code) {
        // v5 resolved with Cognito's VerifySoftwareToken response,
        // `{ Status: 'SUCCESS', Session }`, and setmfa.vue /
        // UserProfileModal.vue gate on `verify.Status == 'SUCCESS'`.
        // v6 resolves with void and throws on a bad code, so restore the
        // v5 shape; a failure still propagates as the thrown error.
        await verifyTOTPSetup({ code })
        return { Status: 'SUCCESS' }
    },

    async setPreferredMFA(_user, type) {
        // v5 resolved with the string 'SUCCESS'; UserProfileModal.vue
        // checks `disabled == 'SUCCESS'` after disabling. v6 resolves
        // with void, so return the v5 value once the call succeeds.
        if (type === 'TOTP') {
            await updateMFAPreference({ totp: 'PREFERRED' })
        } else if (type === 'SMS') {
            await updateMFAPreference({ sms: 'PREFERRED' })
        } else {
            // 'NOMFA' / null
            await updateMFAPreference({ totp: 'DISABLED', sms: 'DISABLED' })
        }
        return 'SUCCESS'
    },

    completeNewPassword(_user, newPassword, requiredAttributes) {
        return v6ConfirmSignIn({
            challengeResponse: newPassword,
            options: requiredAttributes
                ? { userAttributes: requiredAttributes }
                : undefined,
        })
    },

    // v6 removed this — the email/phone verification status check was
    // baked into the post-sign-in nextStep flow. Callers only care that
    // it resolves so the subsequent signedIn event fires.
    verifiedContact(_user) {
        return Promise.resolve({ verified: {}, unverified: {} })
    },

    federatedSignIn(opts) {
        const provider = normalizeProvider(opts.provider)
        return signInWithRedirect({
            provider,
            customState: opts.customState,
        })
    },
}

export default AuthShim
